import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { notification } from "../../utils/Notifications.tsx";
import { getPermissionById, updatePermission, uploadFilesToPermission } from "../../services/permissions.service.ts";
import { getScheduleByPartnerAndDate } from "../../services/branch-schedule.service.ts";
import PermissionFormEditFiles from "../../components/permissions/PermissionFormEditFiles.tsx";
import PermissionFormEditFilesCreate from "../../components/permissions/PermissionFormEditFilesCreate.tsx";
import PermissionFormEdit from "../../components/permissions/PermissionFormEdit.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const EditPermission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(null);
  const [reloadFiles, setReloadFiles] = useState(false);
  const [partnerSchedule, setPartnerSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const isPartner = true;
  const isCreate = false;

  const validationSchema = Yup.object().shape({
    description: Yup.string().required("La descripción es obligatoria"),
    startRequested: Yup.date().required("La fecha del permiso es obligatoria"),
    daysRequested: Yup.number().required("Campo requerido").min(1).max(1),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      description: "",
      startRequested: "",
      daysRequested: 1,
      permissionFiles: [],
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const permissionData = {
          description: values.description,
          startRequested: dayjs(values.startRequested).unix(),
          endRequested: dayjs(values.startRequested).unix(), // Misma fecha
          daysRequested: values.daysRequested,
        };

        await updatePermission(Number(id), permissionData);

        // Subir archivos si hay nuevos
        if (values.permissionFiles && values.permissionFiles.length > 0) {
          await uploadFilesToPermission(Number(id), values.permissionFiles);
        }

        notification("Éxito", "Permiso actualizado correctamente", "success");
        navigate(-1);
      } catch (error) {
        console.error("Error al actualizar el permiso:", error);
        notification("Error", "No se pudo actualizar el permiso", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchPermission = async () => {
    setLoading(true);
    try {
      const response = await getPermissionById(Number(id));
      setPermission(response);
      formik.setValues({
        description: response.description || "",
        startRequested: response.startRequested ? dayjs.unix(response.startRequested).format("YYYY-MM-DD") : "",
        daysRequested: response.daysRequested || 1,
        permissionFiles: [],
      });
    } catch (error) {
      console.error("Error al cargar el permiso:", error);
      notification("Error", "No se pudo cargar el permiso", "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSchedule = async () => {
    if (!permission?.partner?.id || !permission?.startRequested) return;
    
    setLoadingSchedule(true);
    try {
      const formattedDate = dayjs.unix(permission.startRequested).format("YYYY-MM-DD");
      const schedule = await getScheduleByPartnerAndDate(permission.partner.id, formattedDate);
      setPartnerSchedule(schedule);
    } catch (error) {
      console.error("Error al cargar el horario del partner:", error);
      setPartnerSchedule(null);
    } finally {
      setLoadingSchedule(false);
    }
  };

  useEffect(() => {
    fetchPermission();
  }, [id]);

  useEffect(() => {
    if (permission) {
      fetchPartnerSchedule();
    }
  }, [permission]);

  const handleReloadFiles = () => {
    setReloadFiles(!reloadFiles);
    fetchPermission();
  };

  const getPermissionStatus = () => {
    if (!permission) return "";
    if (permission.approved) return "APROBADO";
    if (permission.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const getStatusColor = () => {
    if (!permission) return "bg-secondary";
    if (permission.approved) return "bg-success";
    if (permission.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const isDisabled = permission && (permission.approved || permission.isRejected);

  const formatScheduleTime = (time: string) => {
    if (!time) return "-";
    // Extraer solo la primera parte del tiempo (antes del guión)
    return time.split(' - ')[0] || time;
  };

  const formatScheduleDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("dddd, D [de] MMMM [de] YYYY");
  };

  if (!permission) {
    return (
      <Layout>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Title
        baseTitle="Editar permiso"
        basePath={`/colaboradores/editar-permiso/${id}`}
        title="Información del colaborador"
      />

      {/* Estado del permiso */}
      <div className="card border-0 m-4 rounded">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Estado del Permiso</h5>
            <span className={`badge ${getStatusColor()} fs-6 px-3 py-2`}>
              {getPermissionStatus()}
            </span>
          </div>
          
          {/* Información de táctica de juego */}
          {permission.requireTacticRelease && (
            <div className="alert alert-warning mt-3 mb-0">
              <i className="fas fa-calendar me-2"></i>
              <strong>Eliminado de la táctica de juego:</strong> Este permiso ha sido eliminado de la táctica de juego para que pueda ser cubierto por otra persona.
            </div>
          )}
          
          {isDisabled && (
            <div className="alert alert-info mt-3 mb-0">
              <i className="fas fa-info-circle me-2"></i>
              Este permiso ya ha sido procesado y no puede ser modificado.
            </div>
          )}
        </div>
      </div>

      {/* Información del horario del partner */}
      {loadingSchedule ? (
        <div className="card border-0 m-4 rounded">
          <div className="card-body">
            <div className="text-center">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Cargando horario...</span>
              </div>
              <span className="ms-2">Verificando horario del colaborador...</span>
            </div>
          </div>
        </div>
      ) : partnerSchedule && partnerSchedule.length > 0 ? (
        <div className="card border-0 m-4 rounded">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="fas fa-clock me-2 text-warning"></i>
              Horario del Colaborador para el {formatScheduleDate(permission.startRequested ? dayjs.unix(permission.startRequested).format("YYYY-MM-DD") : "")}
            </h6>
            <div className="table-responsive">
              <table className="table table-sm table-borderless bg-light rounded-3 overflow-hidden shadow-sm">
                <thead className="bg-secondary text-white">
                  <tr>
                    <th className="border-0 py-2 px-3 text-center fw-semibold small">
                      <i className="fas fa-user-tie me-1"></i>Posición
                    </th>
                    <th className="border-0 py-2 px-3 text-center fw-semibold small">
                      <i className="fas fa-clock me-1"></i>Horario
                    </th>
                    <th className="border-0 py-2 px-3 text-center fw-semibold small">
                      <i className="fas fa-calendar-day me-1"></i>Turno
                    </th>
                    <th className="border-0 py-2 px-3 text-center fw-semibold small">
                      <i className="fas fa-building me-1"></i>Sucursal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {partnerSchedule.map((schedule, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-light'}>
                      <td className="border-0 py-2 px-3 text-center fw-medium text-dark small">
                        {schedule.positionName || "-"}
                      </td>
                      <td className="border-0 py-2 px-3 text-center">
                        <span className="badge bg-secondary text-white px-2 py-1 rounded-pill small">
                          <i className="fas fa-clock me-1"></i>
                          {formatScheduleTime(schedule.startTime)} - {formatScheduleTime(schedule.endTime)}
                        </span>
                      </td>
                      <td className="border-0 py-2 px-3 text-center">
                        <span className="badge bg-dark text-white px-2 py-1 rounded-pill small">
                          {schedule.shift || "-"}
                        </span>
                      </td>
                      <td className="border-0 py-2 px-3 text-center fw-medium text-secondary small">
                        {schedule.branch?.name || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="alert alert-warning mt-3 mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Atención:</strong> El colaborador tiene horarios asignados para esta fecha. 
              {permission.requireTacticRelease && " Al aprobar este permiso, será eliminado automáticamente de estos horarios."}
            </div>
          </div>
        </div>
      ) : (
        <div className="card border-0 m-4 rounded">
          <div className="card-body">
            <div className="alert alert-success mb-0">
              <i className="fas fa-check-circle me-2"></i>
              <strong>Sin conflictos:</strong> El colaborador no tiene horarios asignados para el {formatScheduleDate(permission.startRequested ? dayjs.unix(permission.startRequested).format("YYYY-MM-DD") : "")}.
            </div>
          </div>
        </div>
      )}

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Datos generales del permiso para editar
          </PanelHeader>
          <PermissionFormEdit
            formik={formik}
            isPartner={isPartner}
            isCreate={isCreate}
            isDisabled={isDisabled}
          />
        </Panel>
      </div>

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Archivos adjuntos del permiso
          </PanelHeader>
          <PermissionFormEditFiles
            permission={permission}
            reloadFiles={handleReloadFiles}
            isDisabled={isDisabled}
          />
        </Panel>
      </div>

      {/* {!isDisabled && (
        <div className="card border-0 m-4 rounded">
          <Panel>
            <PanelHeader noButton={true} className="bg-azul">
              Agregar nuevos archivos
            </PanelHeader>
            <PermissionFormEditFilesCreate
              formik={formik}
              reloadFiles={handleReloadFiles}
            />
          </Panel>
          </div>
      )} */}

      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate(-1)}
              title="Cancelar"
              variant="secondary"
              type="button"
              disabled={loading}
            />
          </div>
          {!isDisabled ? (
            <div className="col-md-3">
              <Button
                onClick={() => formik.handleSubmit()}
                title="Guardar"
                variant="azul"
                disabled={loading}
                type="submit"
              />
            </div>
          ) : (
            <div className="col-md-3">
              <Button
                onClick={() => navigate(-1)}
                title={`Permiso ${getPermissionStatus().toLowerCase()}`}
                variant="secondary"
                type="button"
                disabled={true}
              />
            </div>
          )}
        </div>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default EditPermission; 